import { useState } from "react";
import { Box, Tooltip, Chip, Divider, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useTranslation } from '../hooks/useTranslation';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(0.5),
    minWidth: 150,
    maxWidth: "100%",
  },
  possible_values: {
    display: "flex",
    flexDirection: "column",
    width: "fit-content",
    backgroundColor: "var(--ikaro-bg-surface)",
    border: "1px solid " + theme.palette.info.main,
    borderRadius: "6px",
    marginLeft: 3,
    maxHeight: 120,
    color: theme.palette.info.main,
    overflow: "auto",
    padding: "4px",
  },
  primary_color: {
    color: theme.palette.primary.main,
  },
  info_color: {
    color: theme.palette.info.main,
  },
}));

const renderLabel = (
  keyName,
  label,
  type,
  description,
  possible_values,
  classes,
  t
) => (
  <div>
    <div
      style={{
        display: "flex",
        alignItems: "center",
        marginTop: 4,
        marginBottom: 4,
        marginLeft: 4,
        maxWidth: "98%",
        flexWrap: "nowrap", // Prevents line break
        overflow: "hidden",
      }}
    >
      {label && <span style={{ whiteSpace: "nowrap" }}>{label}</span>}
      {keyName && (
        <Chip
          style={{ marginLeft: 3 }}
          label={keyName}
          size="small"
          color="primary"
          variant="outlined"
        />
      )}
      {type && (
        <Chip
          style={{ marginLeft: 3 }}
          label={type}
          size="small"
          color="secondary"
          variant="outlined"
        />
      )}
      {description && (
        <Tooltip title={description}>
          <span
            className="ml-8"
            style={{
              color: "var(--ikaro-gray-dark)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {description}
          </span>
        </Tooltip>
      )}
    </div>
    {possible_values && (
      <Box className={classes.possible_values}>
        <span>{t('possibleValues')}</span>
        {possible_values.map((item, idx) => (
          <li key={idx}>{item}</li>
        ))}
      </Box>
    )}
  </div>
);

function TreeNode({
  data,
  keyName,
  classes,
  depth = 0,
  openItems,
  handleToggle,
  disableBorder = false,
}) {
  const { t } = useTranslation('common');
  
  // Se não for objeto, mostra valor simples
  if (typeof data !== "object" || data === null) {
    return <span>{String(data)}</span>;
  }

  // Extrai campos especiais
  const { label, type, description, possible_values } = data;
  const childEntries = Object.entries(data).filter(
    ([key]) =>
      key !== "label" &&
      key !== "type" &&
      key !== "description" &&
      key !== "possible_values"
  );

  if (childEntries.length === 0) {
    // If there are no children, render only the label/type/description if present
    return renderLabel(
      keyName,
      label,
      type,
      description,
      possible_values,
      classes,
      t
    );
  }

  // Verifica se é array ou objeto
  const isArray = Array.isArray(data);

  return (
    <div
      className="text-left"
      style={{
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        marginLeft: !disableBorder ? depth * 10 : "",
        borderLeft: !disableBorder ? "2px solid var(--ikaro-gray-medium)" : "",
      }}
    >
      {isArray
        ? data.map((item, idx) => (
            <div key={idx}>
              <TreeNode
                data={item}
                depth={depth + 1}
                classes={classes}
                openItems={openItems}
                handleToggle={handleToggle}
                disableBorder={true}
              />
            </div>
          ))
        : childEntries.map(([key, value]) => (
            <div key={key}>
              {!label && !("label" in value) && (
                <Chip
                  label={
                    Array.isArray(value)
                      ? key + " [" + value.length + "]"
                      : key + " { }"
                  }
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    if (Array.isArray(value)) {
                      handleToggle(key);
                    }
                  }}
                />
              )}
              {!openItems[key] && (
                <TreeNode
                  data={value}
                  keyName={key}
                  depth={depth + 1}
                  classes={classes}
                  openItems={openItems}
                  handleToggle={handleToggle}
                />
              )}
            </div>
          ))}
    </div>
  );
}
export default function JSONViewer({ data }) {
  const classes = useStyles();
  const { t } = useTranslation('common');
  const [openItems, setOpenItems] = useState({});

  const handleToggle = (key) => {
    setOpenItems((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Pass openItems and handleToggle to TreeNode
  return (
    <div className={classes.container}>
      <Typography variant="h6" gutterBottom align="left">
        <Box
          fontWeight="fontWeightBold"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <span>{t('inputFields')}</span>
        </Box>
        <Divider style={{ borderBottom: "2px solid var(--ikaro-gray-medium)" }} />
      </Typography>
      <TreeNode
        data={data}
        classes={classes}
        openItems={openItems}
        handleToggle={handleToggle}
      />
    </div>
  );
}
